import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router';
import { HOME_PATH } from '@/shared/config';
import { BrandLogo } from '@/shared/ui';
import { AuthBrandPanel } from './auth-brand-panel';

interface AuthPageShellProps {
  /** 크게 보이는 영문 제목(예: "Sign in") — 보조기기에는 screenTitle 을 읽힌다. */
  readonly title: string;
  /** 한국어 화면 이름(예: "로그인") — h1 의 접근 가능한 이름. */
  readonly screenTitle: string;
  readonly description: string;
  /** 왼쪽 브랜드 면의 큰 문구 — 줄을 직접 나누려면 <br /> 를 넣는다. */
  readonly brandHeadline: ReactNode;
  readonly brandDescription: string;
  readonly children: ReactNode;
}

/**
 * 로그인·회원가입·비밀번호 재설정의 전체 화면 틀 — 헤더·푸터 없이 화면 전체를 쓴다.
 * 넓은 화면은 두 칸: 왼쪽 브랜드 면(HOME 배너 사진 + 흑연 그라데이션)이 화면 높이를 채우고,
 * 오른쪽에 큰 영문 제목과 내용이 가운데 선다. 좁은 화면은 내용만 쌓는다.
 * 헤더가 없으니 위쪽 로고(좁은 화면)·"홈으로"가 돌아갈 길이다.
 */
export const AuthPageShell = ({
  title,
  screenTitle,
  description,
  brandHeadline,
  brandDescription,
  children,
}: AuthPageShellProps): ReactElement => (
  // 한국어는 어절 단위로 줄을 바꾼다 — 다른 고객 화면과 같은 규칙.
  <div className="grid min-h-dvh bg-background break-keep lg:grid-cols-[1.1fr_1fr]">
    {/* 넓은 화면에서만 — 화면 높이에 붙여 내용이 길어져도 사진이 따라 내려온다. */}
    <div className="sticky top-0 hidden h-dvh lg:block">
      <AuthBrandPanel headline={brandHeadline} description={brandDescription} />
    </div>

    <section className="flex flex-col px-5 py-6 sm:px-8 xl:px-14">
      <div className="flex items-center justify-between lg:justify-end">
        {/* 넓은 화면은 왼쪽 브랜드 면에 로고가 있어 여기서는 감춘다. 링크 이름은 로고 SVG 의 aria-label 이 준다. */}
        <Link to={HOME_PATH} className="block lg:hidden">
          <BrandLogo className="h-7 sm:h-8" />
        </Link>
        <Link
          to={HOME_PATH}
          className="text-[13px] text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        >
          홈으로
        </Link>
      </div>

      <div className="flex flex-1 items-center justify-center py-12 sm:py-16">
        <div className="w-full max-w-md">
          <header className="flex flex-col gap-3">
            <h1 className="text-[clamp(2.5rem,5vw,3.5rem)] leading-none font-bold tracking-[-0.03em]">
              <span aria-hidden>{title}</span>
              <span className="sr-only">{screenTitle}</span>
            </h1>
            <p className="text-[15px] text-muted-foreground">{description}</p>
          </header>

          <div className="mt-10">{children}</div>
        </div>
      </div>
    </section>
  </div>
);
